import {
  Component,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ElementRef,
  ViewChild,
  TemplateRef,
  Renderer2,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { environment } from '../../../environments/environment';
import { MatIcon, MatIconRegistry } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Subscription } from 'rxjs';
import { MarkdownModule } from 'ngx-markdown';
import { CommonExplainModule } from '../../common-explain/common-explain.module';

const SMART_SEARCH_SESSIONS_KEY = 'smart-search-sessions';

export enum StepStatusEnum {
  IN_PROGRESS = 'in_progress',
  WEBSOCKET_STARTED = 'websocket_started',
  SCORING = 'scoring',
  SYNTHESIS = 'synthesis',
  COMPLETED = 'completed',
  ERROR = 'error',
}

interface SmartSearchResult {
  id?: string;
  title?: string;
  synthesis?: string;
  stats?: {
    nb_retrieved_documents?: number;
    nb_quoted_documents?: number;
    nb_scored_documents?: number;
    nb_retrieved_per_doc_type?: {
      article?: number;
      admin_doc?: number;
      web_content?: number;
    };
    nb_scored_per_doc_type?:{
      article?: number;
      admin_doc?: number;
    }
    min_publication_date_scored?: string;
    max_publication_date_scored?: string;
  };
  resources?: any[];
  web_search_result?: {
    content: string;
    sources?: any[];
  };
  error?: any;
}

interface TooltipHandler {
  button: HTMLElement;
  tooltip: HTMLElement;
  showTooltip: () => void;
  hideTooltip: () => void;
  scrollHandler: () => void;
  resizeHandler: () => void;
}

@Component({
  selector: 'app-smart-search',
  standalone: true,
  templateUrl: './smart-search.component.html',
  styleUrls: ['./smart-search.component.scss'],
  imports: [
    MatIcon,
    TranslateModule,
    NgxSkeletonLoaderModule,
    MarkdownModule,
    CommonModule,
    CommonExplainModule,
  ],
})
export class SmartSearchComponent implements OnInit, OnDestroy, AfterViewInit {
  result: SmartSearchResult | null = null;
  step: StepStatusEnum = StepStatusEnum.IN_PROGRESS;
  taskId: string | null = null;
  websocket: WebSocket | null = null;
  errorTitle: string | null = null;
  errorContent: string | null = null;

  showAllPressArticles = false;
  showAllAdministrativeDocs = false;
  showAllWebContent = false;

  pressArticles: any[] = [];
  administrativeDocs: any[] = [];
  webContent: any[] = [];

  private queryParamsSubscription?: Subscription;
  private originalTitle = '';
  private blinkingInterval?: ReturnType<typeof globalThis.setInterval>;
  private blinkingTimeout?: ReturnType<typeof globalThis.setTimeout>;
  private tooltipHandlers: TooltipHandler[] = [];
  private visibilityChangeHandler?: () => void;

  @ViewChild('synthesisContainer', { static: false })
  synthesisContainer?: ElementRef;
  @ViewChild('referenceBadge', { static: false, read: TemplateRef })
  referenceBadgeTemplate?: TemplateRef<any>;

  protected readonly StepStatusEnum = StepStatusEnum;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private renderer: Renderer2,
    private translateService: TranslateService,
    private matIconRegistry: MatIconRegistry
  ) {}

  ngOnInit(): void {
    this.originalTitle = document.title;
    this.updatePageTitle();
    this.setupVisibilityListener();
    this.queryParamsSubscription = this.route.queryParams.subscribe(
      async (params) => {
        if (this.websocket?.readyState === WebSocket.OPEN) {
          return;
        }
        this.taskId = params['taskId'] || null;
        if (this.taskId) {
          // Store taskId in sessionStorage so it can be retrieved when opening documents
          sessionStorage.setItem('smartSearch_taskId', this.taskId);
          const s3Url = this.buildS3Url(this.taskId);
          const fetched = await this.tryFetchFromS3(s3Url);
          if (!fetched) {
            this.startSmartSearch();
          }
        } else {
          this.startSmartSearch();
        }
      }
    );
  }

  ngAfterViewInit(): void {
    this.setupSourceTooltips();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  // ========== Computed Properties ==========

  get totalDocuments(): number {
    return this.result?.stats?.nb_scored_documents || 0;
  }

  get articleCount(): number {
    return this.result?.stats?.nb_scored_per_doc_type?.article || 0;
  }

  get adminDocCount(): number {
    return this.result?.stats?.nb_scored_per_doc_type?.admin_doc || 0;
  }

  get hasDocumentsBreakdown(): boolean {
    return this.articleCount > 0 || this.adminDocCount > 0;
  }

  get formattedMinDate(): string {
    return this.result?.stats?.min_publication_date_scored
      ? this.formatDate(this.result.stats.min_publication_date_scored)
      : '';
  }

  get formattedMaxDate(): string {
    return this.result?.stats?.max_publication_date_scored
      ? this.formatDate(this.result.stats.max_publication_date_scored)
      : '';
  }

  get hasPeriod(): boolean {
    return !!(
      this.result?.stats?.min_publication_date_scored &&
      this.result?.stats?.max_publication_date_scored
    );
  }

  get documentsPlural(): string {
    return this.totalDocuments > 1 ? 's' : '';
  }

  get isEmptySynthesis(): boolean {
    return (
      this.step === StepStatusEnum.COMPLETED &&
      !!this.result &&
      !this.result.synthesis?.trim()
    );
  }

  get shouldShowErrorState(): boolean {
    return (
      (this.step === StepStatusEnum.ERROR && !!this.errorTitle) ||
      this.isEmptySynthesis
    );
  }

  get isNoRelevantDocumentsError(): boolean {
    return (
      !!this.errorTitle &&
      this.errorTitle ===
        'search.smart-search-result.no-relevant-documents-title'
    );
  }

  get totalResources(): number {
    return this.result?.stats?.nb_quoted_documents || 0;
  }

  // ========== Resource Lists ==========

  get visiblePressArticles(): any[] {
    return this.showAllPressArticles
      ? this.pressArticles
      : this.pressArticles.slice(0, 5);
  }

  get visibleAdministrativeDocs(): any[] {
    return this.showAllAdministrativeDocs
      ? this.administrativeDocs
      : this.administrativeDocs.slice(0, 5);
  }

  get visibleWebContent(): any[] {
    return this.showAllWebContent
      ? this.webContent
      : this.webContent.slice(0, 5);
  }

  get hasMorePressArticles(): boolean {
    return this.pressArticles.length > 5;
  }

  get hasMoreAdministrativeDocs(): boolean {
    return this.administrativeDocs.length > 5;
  }

  get hasMoreWebContent(): boolean {
    return this.webContent.length > 5;
  }

  // ========== Translation Keys ==========

  getSourceTranslationKey(sourceType: string, count: number): string {
    const isPlural = count > 1;
    const keys: Record<string, { singular: string; plural: string }> = {
      article: {
        singular: 'search.smart-search-result.article-of-press',
        plural: 'search.smart-search-result.articles-of-press',
      },
      admin_doc: {
        singular: 'search.smart-search-result.administrative-publication',
        plural: 'search.smart-search-result.administrative-publications',
      },
      web_content: {
        singular: 'search.smart-search-result.web-content',
        plural: 'search.smart-search-result.web-contents',
      },
    };

    const sourceKeys = keys[sourceType];
    if (!sourceKeys) {
      return '';
    }

    return isPlural ? sourceKeys.plural : sourceKeys.singular;
  }

  // ========== Public Methods ==========

  retry(): void {
    this.resetState();
    const sessionId = this.route.snapshot.queryParams['sessionId'];
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: sessionId ? { sessionId } : {},
      replaceUrl: true,
    });
    this.startSmartSearch();
  }

  togglePressArticles(): void {
    this.showAllPressArticles = !this.showAllPressArticles;
    setTimeout(() => this.setupSourceTooltips(), 0);
  }

  toggleAdministrativeDocs(): void {
    this.showAllAdministrativeDocs = !this.showAllAdministrativeDocs;
    setTimeout(() => this.setupSourceTooltips(), 0);
  }

  toggleWebContent(): void {
    this.showAllWebContent = !this.showAllWebContent;
    setTimeout(() => this.setupSourceTooltips(), 0);
  }

  truncateTitle(title: string, maxLength: number = 50): string {
    return title?.length > maxLength
      ? title.substring(0, maxLength) + '...'
      : title;
  }

  removeEmTags(html: string): string {
    if (!html) {
      return '';
    }
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    tempDiv.querySelectorAll('em').forEach((em) => {
      const textNode = document.createTextNode(em.textContent || '');
      em.parentNode?.replaceChild(textNode, em);
    });
    return tempDiv.innerHTML;
  }

  getResourceIndex(resource: any): number {
    if (!resource?.synthesis_index) {
      return 0;
    }
    const synthesisIndex = String(resource.synthesis_index);
    const parts = synthesisIndex.split('.');
    const mainIndex = Number.parseInt(parts[0], 10);
    return Number.isNaN(mainIndex) ? 0 : mainIndex;
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('fr-FR', { month: 'long' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  openUrl(url: string): void {
    // Add taskId and sessionId to URL if not already present (for admin docs from synthesis)
    if (url && url.includes('/admin-doc/')) {
      const sessionId = this.route.snapshot.queryParams['sessionId'];
      let finalUrl = url;

      // Check if URL already has query params
      const hasQueryParams = url.includes('?');
      let separator = hasQueryParams ? '&' : '?';

      if (this.taskId && !url.includes('taskId=')) {
        finalUrl += `${separator}taskId=${this.taskId}`;
        // Update separator for next param
        separator = '&';
      }
      if (sessionId && !url.includes('sessionId=')) {
        finalUrl += `${separator}sessionId=${sessionId}`;
      }

      globalThis.open(finalUrl, '_blank');
    } else {
      globalThis.open(url, '_blank');
    }
  }

  getStepTranslationKey(): string {
    const stepKeys: Record<string, string> = {
      [StepStatusEnum.IN_PROGRESS]: 'search.smart-search-result.in-progress',
      [StepStatusEnum.WEBSOCKET_STARTED]:
        'search.smart-search-result.websocket-started',
      [StepStatusEnum.SCORING]: 'search.smart-search-result.scoring',
      [StepStatusEnum.SYNTHESIS]: 'search.smart-search-result.synthesis',
    };
    return stepKeys[this.step] || '';
  }

  getDocumentBreakdown(): string {
    const parts: string[] = [];
    if (this.articleCount > 0) {
      const translatedKey = this.translateService.instant(
        this.getSourceTranslationKey('article', this.articleCount)
      );
      parts.push(`${this.articleCount} ${translatedKey}`);
    }
    if (this.adminDocCount > 0) {
      const translatedKey = this.translateService.instant(
        this.getSourceTranslationKey('admin_doc', this.adminDocCount)
      );
      parts.push(`${this.adminDocCount} ${translatedKey}`);
    }
    return parts.join(' et ');
  }

  onMarkdownRendered(event: any): void {
    const container = this.synthesisContainer?.nativeElement;
    if (!container || !this.result?.synthesis) {
      return;
    }

    const markdownElement =
      container.querySelector('markdown') ||
      container.querySelector('.markdown-content') ||
      container.firstElementChild;
    if (!markdownElement) {
      return;
    }

    markdownElement.innerHTML = this.convertSynthesisStyles(
      markdownElement.innerHTML
    );
    this.replaceReferencesWithButtons(markdownElement as HTMLElement);
    this.setupSynthesisTooltips(container);
    container.addEventListener('click', this.handleButtonClick);
  }

  // ========== WebSocket & Search ==========

  private startSmartSearch(): void {
    const query = this.buildSearchQuery();
    if (!query) {
      return;
    }

    this.resetState();
    this.step = StepStatusEnum.IN_PROGRESS;
    this.updatePageTitle();

    this.websocket = new WebSocket(environment.smartSearchApiGatewayUrl);
    this.websocket.addEventListener('open', () => {
      this.websocket?.send(JSON.stringify(query));
      this.step = StepStatusEnum.WEBSOCKET_STARTED;
      this.updatePageTitle();
    });

    this.websocket.addEventListener('error', () => {
      this.step = StepStatusEnum.ERROR;
      this.updatePageTitle();
    });

    this.websocket.addEventListener('message', (event) => {
      this.handleWebSocketMessage(JSON.parse(event.data));
    });
  }

  private buildSearchQuery(): any {
    const apiToken = localStorage.getItem('api_token');
    if (!apiToken) {
      console.error('No API token found');
      return null;
    }

    if (
      this.taskId &&
      (this.step == StepStatusEnum.COMPLETED ||
        this.step == StepStatusEnum.ERROR ||
        this.step == StepStatusEnum.IN_PROGRESS)
    ) {
      return {
        action: 'smart-search',
        task_id: this.taskId,
        api_token: apiToken,
      };
    }

    const sessionId = this.route.snapshot.queryParams['sessionId'];
    if (!sessionId) {
      console.error('No sessionId found in URL');
      return null;
    }

    const config = this.loadSessionConfig(sessionId);
    if (!config) {
      return null;
    }

    let prompt: string = config.prompt;
    Object.entries<string>(config.promptMap).forEach(([key, value]) => {
      prompt = prompt.replace(`\[${key}\]`, value);
    });

    const query: any = {
      action: 'smart-search',
      api_token: apiToken,
      prompt: prompt,
      filters: {
        document_types: this.getDocumentTypes(config.sources),
        perimeter: config.perimeter || [],
        territories: config.territories || [],
        sources: {
          article: config.pressSources || [],
          admin_doc: config.adminDocSources || [],
        },
      },
    };

    if (config.keywords) {
      query.filters.text = config.keywords;
    }
    if (config.topicId) {
      query.filters.topic_id = config.topicId;
    }
    if (config.impacterId) {
      query.filters.impacter_id = Number(config.impacterId);
      if (!config.keywords) {
        query.filters.text = '';
      }
    }

    console.log('query', query);

    return query;
  }

  private loadSessionConfig(sessionId: string): any {
    const sessionsStr = sessionStorage.getItem(SMART_SEARCH_SESSIONS_KEY) || '{}';
    const sessions = JSON.parse(sessionsStr);
    const session = sessions[sessionId];

    if (!session) {
      console.error('Session not found for sessionId:', sessionId);
      return null;
    }

    return session;
  }

  private getDocumentTypes(sources: string[] = []): string[] {
    const types: string[] = [];
    if (sources.includes('press')) {
      types.push('article');
    }
    if (sources.includes('adminDoc')) {
      types.push('admin_doc');
    }
    if (sources.includes('web')) {
      types.push('web_content');
    }
    return types;
  }

  private handleWebSocketMessage(message: any): void {
    switch (message.type) {
      case 'step':
        this.step = message.message;
        this.updatePageTitle();
        break;
      case 'id':
        this.updateTaskIdInUrl(message.message);
        break;
      case 'result':
        this.handleResult(message.message);
        break;
      case 'result_url':
        this.fetchResultFromUrl(message.message);
        break;
      case 'error':
        this.handleError(message.message);
        break;
      case 'done':
        this.websocket?.close();
        this.step = StepStatusEnum.COMPLETED;
        this.updatePageTitle();
        break;
    }
  }

  private handleResult(message: any): void {
    if (typeof message === 'string') {
      try {
        this.result = JSON.parse(message);
      } catch {
        this.result = { synthesis: message };
      }
    } else {
      this.result = message;
    }
    this.updateFilteredResources();
    this.step = StepStatusEnum.COMPLETED;
    this.updatePageTitle();
    setTimeout(() => this.setupSourceTooltips(), 0);
  }

  private checkIsNoRelevantDocumentsError(error: any): boolean {
    return (
      error === 'NO_RELEVANT_DOCUMENTS' || error === 'No relevant documents'
    );
  }

  private handleError(message: any): void {
    if (this.checkIsNoRelevantDocumentsError(message)) {
      this.errorTitle =
        'search.smart-search-result.no-relevant-documents-title';
      this.errorContent =
        'search.smart-search-result.no-relevant-documents-content';
    } else {
      this.errorTitle = 'search.smart-search-result.error-default-title';
      this.errorContent = null;
    }

    this.step = StepStatusEnum.ERROR;
    this.websocket?.close();
    this.updatePageTitle();
  }

  private updateTaskIdInUrl(taskId: string): void {
    this.taskId = taskId;
    const sessionId = this.route.snapshot.queryParams['sessionId'];

    // Store taskId in session config so it can be retrieved per session
    if (sessionId) {
      const sessionsStr = sessionStorage.getItem(SMART_SEARCH_SESSIONS_KEY) || '{}';
      const sessions = JSON.parse(sessionsStr);
      if (sessions[sessionId]) {
        sessions[sessionId].taskId = taskId;
        sessionStorage.setItem(SMART_SEARCH_SESSIONS_KEY, JSON.stringify(sessions));
      }
    }

    // Also store in sessionStorage for backward compatibility (when taskId is in URL)
    sessionStorage.setItem('smartSearch_taskId', taskId);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { taskId, sessionId },
      replaceUrl: true,
    });
  }

  private buildS3Url(taskId: string): string {
    const baseUrl = environment.s3SmartSearchBaseUrl;
    return `${baseUrl}/${taskId}.json`;
  }

  private async tryFetchFromS3(url: string): Promise<boolean> {
    try {
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();

        if (this.checkIsNoRelevantDocumentsError(data.error)) {
          this.handleError(data.error);
          return true;
        }

        this.result = data;
        this.updateFilteredResources();
        this.step = StepStatusEnum.COMPLETED;
        this.updatePageTitle();
        setTimeout(() => this.setupSourceTooltips(), 0);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error fetching from S3:', error);
      return false;
    }
  }

  private async fetchResultFromUrl(url: string): Promise<void> {
    try {
      const response = await fetch(url);
      this.result = await response.json();
      this.updateFilteredResources();
      this.step = StepStatusEnum.COMPLETED;
      this.updatePageTitle();
      setTimeout(() => this.setupSourceTooltips(), 0);
    } catch (error) {
      console.error('Error fetching result:', error);
      this.step = StepStatusEnum.ERROR;
      this.updatePageTitle();
    } finally {
      this.websocket?.close();
    }
  }

  // ========== Page Title Management ==========

  private updatePageTitle(): void {
    this.stopBlinking();

    if (this.step === StepStatusEnum.ERROR) {
      document.title = this.translateService.instant(
        'search.smart-search-result.page-title-error'
      );
    } else if (this.step === StepStatusEnum.COMPLETED) {
      this.startBlinking();
    } else {
      document.title = this.translateService.instant(
        'search.smart-search-result.page-title-in-progress'
      );
    }
  }

  private startBlinking(): void {
    // If the tab is already visible, don't start blinking
    if (!document.hidden) {
      document.title = this.translateService.instant(
        'search.smart-search-result.page-title-completed'
      );
      return;
    }

    let isGreen = true;
    document.title = this.translateService.instant(
      'search.smart-search-result.page-title-completed'
    );

    this.blinkingInterval = globalThis.setInterval(() => {
      if (!document.hidden) {
        this.stopBlinking();
        document.title = this.translateService.instant(
          'search.smart-search-result.page-title-completed'
        );
        return;
      }
      isGreen = !isGreen;
      document.title = isGreen
        ? this.translateService.instant(
            'search.smart-search-result.page-title-completed'
          )
        : this.translateService.instant(
            'search.smart-search-result.page-title-completed-alt'
          );
    }, 500);

    this.blinkingTimeout = globalThis.setTimeout(() => {
      this.stopBlinking();
      document.title = this.translateService.instant(
        'search.smart-search-result.page-title-completed'
      );
    }, 30000);
  }

  private stopBlinking(): void {
    if (this.blinkingInterval) {
      clearInterval(this.blinkingInterval);
      this.blinkingInterval = undefined;
    }
    if (this.blinkingTimeout) {
      clearTimeout(this.blinkingTimeout);
      this.blinkingTimeout = undefined;
    }
  }

  private setupVisibilityListener(): void {
    this.visibilityChangeHandler = () => {
      if (!document.hidden) {
        this.stopBlinking();
        if (this.step === StepStatusEnum.COMPLETED) {
          document.title = this.translateService.instant(
            'search.smart-search-result.page-title-completed'
          );
        }
      }
    };
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  // ========== Resource Filtering ==========

  private getFilteredResources(docType: string): any[] {
    const allResources = this.result?.resources || [];
    const resources = allResources.filter((r) => {
      return r.document_type === docType && r.synthesis_index;
    });
    const deduplicated = this.deduplicateByMainIndex(resources);
    return this.sortResourcesByIndex(deduplicated);
  }

  private deduplicateByMainIndex(resources: any[]): any[] {
    const getSubIndex = (resource: any): number => {
      const index = String(resource.synthesis_index || '');
      const parts = index.split('.');
      return parts.length > 1 ? Number.parseInt(parts[1], 10) : 0;
    };

    const seen = new Map<number, any>();

    for (const resource of resources) {
      const mainIndex = this.getResourceIndex(resource);
      const existing = seen.get(mainIndex);
      if (!existing) {
        seen.set(mainIndex, resource);
        continue;
      }

      const currentSub = getSubIndex(resource);
      const existingSub = getSubIndex(existing);

      // Keep resource with smaller sub-index
      if (currentSub === 0 || (currentSub > 0 && currentSub < existingSub)) {
        seen.set(mainIndex, resource);
      }
    }

    return [...seen.values()];
  }

  private sortResourcesByIndex(resources: any[]): any[] {
    return resources.sort((a, b) => {
      return this.getResourceIndex(a) - this.getResourceIndex(b);
    });
  }

  // ========== Reference Replacement ==========

  private replaceReferencesWithButtons(container: HTMLElement): void {
    if (!this.result?.resources) {
      return;
    }

    const resourceMap = new Map(
      this.result.resources.map((resource: any) => [
        String(resource.synthesis_index || ''),
        resource,
      ])
    );

    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null
    );

    const textNodes: Array<{ node: Text; parent: Node }> = [];
    let node: Node | null;
    while ((node = walker.nextNode())) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent) {
        const text = node.textContent;
        if (/\[[^\]]+?\]/.test(text)) {
          textNodes.push({ node: node as Text, parent: node.parentNode! });
        }
      }
    }

    textNodes.forEach(({ node, parent }) => {
      const text = node.textContent || '';
      const pattern = /\[([^\]]+?)\]/g;
      const matches: Array<{
        index: number;
        length: number;
        synthesisIndex: string;
        resource: any;
      }> = [];
      let match: RegExpExecArray | null;

      while ((match = pattern.exec(text)) !== null) {
        const synthesisIndex = match[1];
        const resource = resourceMap.get(synthesisIndex);
        if (resource) {
          matches.push({
            index: match.index,
            length: match[0].length,
            synthesisIndex,
            resource,
          });
        }
      }

      if (matches.length === 0) {
        return;
      }

      const sortedMatches = [...matches].sort((a, b) => b.index - a.index);
      const fragment = document.createDocumentFragment();

      sortedMatches.forEach((match, i) => {
        const nextMatch = sortedMatches[i + 1];
        const startIndex = nextMatch
          ? nextMatch.index + nextMatch.length
          : match.index + match.length;
        const endIndex = match.index;

        if (startIndex < endIndex) {
          const textAfter = text.substring(startIndex, endIndex);
          if (textAfter) {
            fragment.insertBefore(
              document.createTextNode(textAfter),
              fragment.firstChild
            );
          }
        }

        const button = this.createButtonElement(
          match.resource.doc_id || '',
          match.resource,
          match.synthesisIndex
        );
        if (button) {
          fragment.insertBefore(button, fragment.firstChild);
        }
      });

      const firstMatch = sortedMatches.at(-1);
      if (firstMatch && firstMatch.index > 0) {
        const textBefore = text.substring(0, firstMatch.index);
        if (textBefore) {
          fragment.insertBefore(
            document.createTextNode(textBefore),
            fragment.firstChild
          );
        }
      }

      node.replaceWith(fragment);
    });
  }

  private createButtonElement(
    docId: string,
    resource: any,
    index: string | number
  ): HTMLElement | null {
    const button = this.renderer.createElement('button');
    this.renderer.addClass(button, 'source-reference-btn');
    this.renderer.addClass(button, 'reference-badge');
    this.renderer.addClass(button, 'source-reference-btn-positioned');
    this.renderer.setAttribute(button, 'data-doc-id', docId);
    this.renderer.setAttribute(button, 'data-url', resource.url || '');
    const textNode = this.renderer.createText(String(index));
    this.renderer.appendChild(button, textNode);

    const title = resource.title || '';
    if (title) {
      const titleWithoutEm = this.removeEmTags(title);
      const tooltip = this.createTooltipElement(
        titleWithoutEm,
        resource.publication_date
          ? this.formatDate(resource.publication_date)
          : '',
        resource.source || null,
        resource.document_type || '',
        resource.excerpt || ''
      );
      if (tooltip) {
        this.renderer.appendChild(button, tooltip);
      }
    }

    return button;
  }

  private createTooltipElement(
    title: string,
    date: string,
    source: string | null,
    documentType: string,
    excerpt: string
  ): HTMLElement | null {
    const tooltip = this.renderer.createElement('div');
    this.renderer.addClass(tooltip, 'source-reference-tooltip');
    this.renderer.addClass(tooltip, 'smart-search-tooltip');
    this.renderer.addClass(tooltip, 'smart-search-tooltip-positioned');

    const titleDiv = this.renderer.createElement('div');
    this.renderer.addClass(titleDiv, 'tooltip-title');
    this.renderer.setProperty(titleDiv, 'innerHTML', title);
    this.renderer.appendChild(tooltip, titleDiv);

    const metaDiv = this.renderer.createElement('div');
    this.renderer.addClass(metaDiv, 'tooltip-meta');

    if (date) {
      const dateDiv = this.renderer.createElement('div');
      this.renderer.addClass(dateDiv, 'tooltip-date');
      this.renderer.addClass(dateDiv, 'tooltip-date-visible');

      const dateIcon = this.renderer.createElement('span');
      this.renderer.addClass(dateIcon, 'icon-placeholder');
      this.renderer.setAttribute(dateIcon, 'data-icon', 'calendar-basic-600');
      this.renderer.appendChild(dateDiv, dateIcon);

      const dateSpan = this.renderer.createElement('span');
      const dateText = this.renderer.createText(date);
      this.renderer.appendChild(dateSpan, dateText);
      this.renderer.appendChild(dateDiv, dateSpan);

      this.renderer.appendChild(metaDiv, dateDiv);
    }

    if (source && documentType !== 'admin_doc') {
      const sourceDiv = this.renderer.createElement('div');
      this.renderer.addClass(sourceDiv, 'tooltip-source');
      this.renderer.addClass(sourceDiv, 'tooltip-source-visible');

      const sourceIcon = this.renderer.createElement('span');
      this.renderer.addClass(sourceIcon, 'icon-placeholder');
      this.renderer.setAttribute(sourceIcon, 'data-icon', 'docs-basic-600');
      this.renderer.appendChild(sourceDiv, sourceIcon);

      const sourceSpan = this.renderer.createElement('span');
      const sourceText = this.renderer.createText(source);
      this.renderer.appendChild(sourceSpan, sourceText);
      this.renderer.appendChild(sourceDiv, sourceSpan);

      this.renderer.appendChild(metaDiv, sourceDiv);
    }

    this.renderer.appendChild(tooltip, metaDiv);

    if (excerpt) {
      const normalizedExcerpt = excerpt
        .replaceAll(/\s+/g, ' ')
        .replaceAll(/>\s+</g, '><')
        .trim();

      const excerptDiv = this.renderer.createElement('div');
      this.renderer.addClass(excerptDiv, 'tooltip-excerpt');
      this.renderer.addClass(excerptDiv, 'tooltip-excerpt-visible');
      this.renderer.setProperty(excerptDiv, 'innerHTML', normalizedExcerpt);
      this.renderer.appendChild(tooltip, excerptDiv);
    }

    return tooltip;
  }

  private convertSynthesisStyles(html: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((heading) => {
      const span = document.createElement('span');
      span.className = 'title';
      span.innerHTML = heading.innerHTML;
      heading.parentNode?.replaceChild(span, heading);
    });

    tempDiv.querySelectorAll('p').forEach((p) => {
      const span = document.createElement('span');
      span.className = 'text';
      span.innerHTML = p.innerHTML;
      p.parentNode?.replaceChild(span, p);
    });

    return tempDiv.innerHTML;
  }

  // ========== Tooltip Management ==========

  private setupSourceTooltips(): void {
    setTimeout(() => {
      const sourceWrappers = document.querySelectorAll(
        '.right-side .source-link-wrapper:not([data-tooltip-setup])'
      );

      sourceWrappers.forEach((wrapper) => {
        const linkWrapper = wrapper as HTMLElement;
        const tooltip = linkWrapper.querySelector(
          '.source-reference-tooltip'
        ) as HTMLElement;
        if (!tooltip) {
          return;
        }

        this.configureTooltip(linkWrapper, tooltip);
        this.registerTooltipHandlers(linkWrapper, tooltip);
      });
    }, 0);
  }

  private configureTooltip(
    linkWrapper: HTMLElement,
    tooltip: HTMLElement
  ): void {
    linkWrapper.dataset['tooltipSetup'] = 'true';
    if (!tooltip.classList.contains('smart-search-tooltip')) {
      tooltip.classList.add(
        'smart-search-tooltip',
        'smart-search-tooltip-positioned'
      );
    }

    if (tooltip.parentElement !== document.body) {
      this.renderer.appendChild(document.body, tooltip);
    }
  }

  private registerTooltipHandlers(
    linkWrapper: HTMLElement,
    tooltip: HTMLElement
  ): void {
    const updateTooltipPosition = () =>
      this.updateTooltipPosition(linkWrapper, tooltip);
    const showTooltip = () => {
      updateTooltipPosition();
      tooltip.classList.add('smart-search-tooltip-visible');
    };
    const hideTooltip = () =>
      tooltip.classList.remove('smart-search-tooltip-visible');
    const scrollHandler = () => {
      if (tooltip.classList.contains('smart-search-tooltip-visible')) {
        updateTooltipPosition();
      }
    };

    linkWrapper.addEventListener('mouseenter', showTooltip);
    linkWrapper.addEventListener('mouseleave', hideTooltip);
    globalThis.addEventListener('scroll', scrollHandler, true);
    globalThis.addEventListener('resize', updateTooltipPosition);

    this.tooltipHandlers.push({
      button: linkWrapper,
      tooltip,
      showTooltip,
      hideTooltip,
      scrollHandler,
      resizeHandler: updateTooltipPosition,
    });
  }

  private updateTooltipPosition(
    linkWrapper: HTMLElement,
    tooltip: HTMLElement
  ): void {
    const rect = linkWrapper.getBoundingClientRect();
    const windowHeight = globalThis.innerHeight;
    const windowWidth = globalThis.innerWidth;
    const tooltipWidth = 360;

    tooltip.classList.add('smart-search-tooltip-measuring');
    const tooltipHeight = tooltip.getBoundingClientRect().height || 200;
    tooltip.classList.remove('smart-search-tooltip-measuring');

    const spaceLeft = rect.left;
    const spaceRight = windowWidth - rect.left;
    const spaceBelow = windowHeight - rect.top;

    let top = rect.top;
    if (spaceBelow < tooltipHeight) {
      top = Math.max(8, windowHeight - tooltipHeight - 8);
    }

    const left =
      spaceLeft < tooltipWidth + 8 && spaceRight > tooltipWidth + 8
        ? rect.right + 8
        : rect.left - tooltipWidth - 8;

    tooltip.style.left = `${Math.max(
      8,
      Math.min(left, windowWidth - tooltipWidth - 8)
    )}px`;
    tooltip.style.top = `${top}px`;
  }

  private setupSynthesisTooltips(container: HTMLElement): void {
    setTimeout(() => {
      container.querySelectorAll('.source-reference-btn').forEach((button) => {
        const btn = button as HTMLElement;
        const tooltip = btn.querySelector(
          '.source-reference-tooltip'
        ) as HTMLElement;
        if (!tooltip) {
          return;
        }

        this.replaceIconPlaceholders(tooltip);
        this.configureTooltip(btn, tooltip);
        this.registerTooltipHandlers(btn, tooltip);
      });
    }, 0);
  }

  private replaceIconPlaceholders(tooltip: HTMLElement): void {
    const placeholders = tooltip.querySelectorAll('.icon-placeholder');
    placeholders.forEach((placeholder) => {
      const iconName = (placeholder as HTMLElement).dataset['icon'];
      if (iconName) {
        const iconUrl = this.matIconRegistry.getNamedSvgIcon(iconName);
        if (iconUrl) {
          iconUrl.subscribe((svg: SVGElement) => {
            const svgElement = svg.cloneNode(true) as SVGElement;
            svgElement.setAttribute('width', '16');
            svgElement.setAttribute('height', '16');
            svgElement.style.width = '16px';
            svgElement.style.height = '16px';
            this.renderer.insertBefore(
              placeholder.parentNode as HTMLElement,
              svgElement,
              placeholder
            );
            this.renderer.removeChild(
              placeholder.parentNode as HTMLElement,
              placeholder
            );
          });
        }
      }
    });
  }

  private handleButtonClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (target.classList.contains('source-reference-btn')) {
      const url = target.dataset['url'];
      if (url) {
        event.preventDefault();
        globalThis.open(url, '_blank');
      }
    }
  };

  // ========== Cleanup ==========

  private resetState(): void {
    this.errorTitle = null;
    this.errorContent = null;
    this.result = null;
    this.taskId = null;
    this.pressArticles = [];
    this.administrativeDocs = [];
    this.webContent = [];
  }

  private updateFilteredResources(): void {
    this.pressArticles = this.getFilteredResources('article');
    this.administrativeDocs = this.getFilteredResources('admin_doc');
    this.webContent = this.getFilteredResources('web_content');
  }

  private cleanup(): void {
    this.stopBlinking();
    document.title = this.originalTitle;
    if (this.visibilityChangeHandler) {
      document.removeEventListener(
        'visibilitychange',
        this.visibilityChangeHandler
      );
      this.visibilityChangeHandler = undefined;
    }
    this.queryParamsSubscription?.unsubscribe();
    this.websocket?.close();

    if (this.synthesisContainer?.nativeElement) {
      this.synthesisContainer.nativeElement.removeEventListener(
        'click',
        this.handleButtonClick
      );
    }

    this.tooltipHandlers.forEach(
      ({
        button,
        tooltip,
        showTooltip,
        hideTooltip,
        scrollHandler,
        resizeHandler,
      }) => {
        button.removeEventListener('mouseenter', showTooltip);
        button.removeEventListener('mouseleave', hideTooltip);
        globalThis.removeEventListener('scroll', scrollHandler, true);
        globalThis.removeEventListener('resize', resizeHandler);
        tooltip.remove();
      }
    );

    this.tooltipHandlers = [];
  }
}
